import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Camera, Mic, Upload, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import {
  createSmartfileSession,
  getSmartfileSession,
} from "@/services/smartfile";
import { processFileUploadUltraFast, processTextInputUltraFast } from "@/services/smartfile-fast";
import { CameraCaptureMulti } from "@/components/smartfile/CameraCaptureMulti";
import { AudioRecorder } from "@/components/smartfile/AudioRecorder";
import { TextInput } from "@/components/smartfile/TextInput";
import { supabase } from "@/integrations/supabase/client";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";

interface Step1UploadProps {
  patientId: string | null;
  onComplete: (sessionId: string) => void;
  initialMethod?: 'camera' | 'audio' | 'file' | 'text' | null;
  onCancel?: () => void;
  compact?: boolean;
  compactVariant?: "default" | "home";
  disableAI?: boolean;
}

export default function Step1Upload({
  patientId,
  onComplete,
  initialMethod,
  onCancel,
  compact = false,
  compactVariant = "default",
  disableAI = false,
}: Step1UploadProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isPacientePortalSmartfile = location.pathname.startsWith("/paciente/smartfile");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState("");
  const [showCamera, setShowCamera] = useState(false);
  const [showRecorder, setShowRecorder] = useState(false);
  const [showTextInput, setShowTextInput] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Verificar status da assinatura para limitação de upload
  const { currentPlan } = useSubscriptionStatus();

  // Detectar hash da URL ou initialMethod e abrir modal correspondente
  useEffect(() => {
    // Se foi passado initialMethod como prop, tem prioridade
    if (initialMethod) {
      if (initialMethod === 'camera') {
        setShowCamera(true);
      } else if (initialMethod === 'audio') {
        setShowRecorder(true);
      } else if (initialMethod === 'file') {
        handleFileUpload();
      } else if (initialMethod === 'text') {
        setShowTextInput(true);
      }
      return;
    }

    // Senão, verifica hash da URL (comportamento atual)
    const hash = window.location.hash.replace('#', '');
    if (hash === 'camera') {
      setShowCamera(true);
      window.location.hash = '';
    } else if (hash === 'audio') {
      setShowRecorder(true);
      window.location.hash = '';
    } else if (hash === 'upload') {
      fileInputRef.current?.click();
      window.location.hash = '';
    }
  }, [initialMethod]);

  const handleFileUpload = async () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Verificar limite de upload múltiplo baseado no plano
    const maxUploads = currentPlan?.max_file_uploads ?? 1;

    // Se múltiplos arquivos foram selecionados
    if (files.length > 1) {
      // Verificar se o plano permite upload múltiplo
      if (maxUploads === 1) {
        toast.error("Seu plano não permite upload múltiplo. Selecione um arquivo por vez.");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      // Verificar limite máximo
      if (maxUploads && files.length > maxUploads) {
        toast.error(`Máximo de ${maxUploads} arquivo(s) por vez. Selecione até ${maxUploads} arquivo(s).`);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }
    }

    // Processar apenas o primeiro arquivo (comportamento atual)
    const file = files[0];
    const validTypes = ["pdf", "jpg", "jpeg", "png", "xlsx", "xls", "csv", "mp3", "wav", "m4a"];
    const ext = file.name.split(".").pop()?.toLowerCase() || "";

    if (!validTypes.includes(ext)) {
      toast.error("Tipo de arquivo não suportado. Use PDF, imagens, planilhas ou áudio.");
      return;
    }

    setUploading(true);
    setProgress(0);
    setProgressText("Iniciando...");

    try {
      // 1. Criar sessão
      setProgressText("Criando sessão...");
      const sessionId = await createSmartfileSession(patientId);

      // 2. Processar arquivo completo
      await processFileUploadUltraFast(sessionId, file, (stage, prog) => {
        setProgressText(stage);
        setProgress(prog);
      }, { skipAIInsights: disableAI });

      // ✅ VALIDAÇÃO: Verificar se o documento é "other" após processamento
      const session = await getSmartfileSession(sessionId);
      if (session.content_type === 'other') {
        // Deletar arquivo do storage se existir
        if (session.file_path) {
          try {
            await supabase.storage
              .from('patient-docs')
              .remove([session.file_path]);
          } catch (storageError) {
            console.error('Erro ao deletar arquivo do storage:', storageError);
          }
        }
        
        // Deletar lab_results associados se existirem
        try {
          await supabase
            .from('lab_results_onboarding')
            .delete()
            .eq('source_session_id', sessionId);
        } catch (labError) {
          console.error('Erro ao deletar lab_results:', labError);
        }
        
        // Deletar a sessão completamente
        try {
          await supabase
            .from('smartfile_sessions')
            .delete()
            .eq('id', sessionId);
        } catch (sessionError) {
          console.error('Erro ao deletar sessão:', sessionError);
        }
        
        toast.error("O arquivo enviado não possui dados clínicos de saúde. Favor verificar.");
        setUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      toast.success("Documento processado com sucesso!");
      onComplete(sessionId);
    } catch (error: any) {
      console.error("Erro no upload:", error);
      toast.error(error.message || "Erro ao processar arquivo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleCameraCapture = () => {
    setShowCamera(true);
  };

  const handlePhotoCapture = async (files: File[]) => {
    // Verificar limite de upload múltiplo baseado no plano
    const maxUploads = currentPlan?.max_file_uploads ?? 1;
    
    // Se for mais de uma foto, processar múltiplas e navegar para tela do paciente
    if (files.length > 1) {
      // Verificar se o plano permite upload múltiplo
      if (maxUploads === 1) {
        toast.error("Seu plano não permite upload múltiplo. Capture uma foto por vez.");
        return;
      }

      // Verificar limite máximo
      if (maxUploads && files.length > maxUploads) {
        toast.error(`Máximo de ${maxUploads} foto(s) por vez. Capture até ${maxUploads} foto(s).`);
        return;
      }

      if (!patientId) {
        toast.error("É necessário selecionar o paciente para poder subir múltiplas fotos de uma única vez");
        // Não fecha o modal para permitir que o usuário exclua as fotos
        return;
      }

      setShowCamera(false);
      setUploading(true);
      setProgress(0);
      setProgressText("Processando múltiplas fotos...");

      try {
        // Processar cada foto criando uma sessão smartfile
        const sessionIds: string[] = [];
        
        const failedFiles: string[] = [];
        
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          setProgressText(`Processando foto ${i + 1} de ${files.length}...`);
          setProgress((i / files.length) * 100);

          const sessionId = await createSmartfileSession(patientId);
          await processFileUploadUltraFast(sessionId, file, (stage, prog) => {
            setProgressText(`Foto ${i + 1}/${files.length}: ${stage}`);
            setProgress((i / files.length) * 100 + (prog / files.length));
          }, { skipAIInsights: disableAI });

          // ✅ VALIDAÇÃO: Verificar se o documento é "other"
          const session = await getSmartfileSession(sessionId);
          if (session.content_type === 'other') {
            // Deletar arquivo do storage se existir
            if (session.file_path) {
              try {
                await supabase.storage
                  .from('patient-docs')
                  .remove([session.file_path]);
              } catch (storageError) {
                console.error('Erro ao deletar arquivo do storage:', storageError);
              }
            }
            
            // Deletar lab_results associados se existirem
            try {
              await supabase
                .from('lab_results_onboarding')
                .delete()
                .eq('source_session_id', sessionId);
            } catch (labError) {
              console.error('Erro ao deletar lab_results:', labError);
            }
            
            // Deletar a sessão completamente
            try {
              await supabase
                .from('smartfile_sessions')
                .delete()
                .eq('id', sessionId);
            } catch (sessionError) {
              console.error('Erro ao deletar sessão:', sessionError);
            }
            
            failedFiles.push(file.name);
            toast.error(`❌ ${file.name} não possui dados clínicos de saúde`, {
              description: 'O arquivo enviado não possui dados clínicos de saúde. Favor verificar.',
              duration: 5000
            });
          } else {
            sessionIds.push(sessionId);
          }
        }
        
        if (failedFiles.length > 0) {
          toast.warning(`${failedFiles.length} arquivo(s) não processado(s): não são documentos laboratoriais`);
        }

        setProgress(100);
        setProgressText("Concluído!");
        toast.success(`${files.length} fotos processadas com sucesso!`);
        
        // Navegar para tela do paciente
        setTimeout(() => {
          navigate(
            isPacientePortalSmartfile
              ? `/paciente/minha-saude/${patientId}`
              : `/app/pacientes/${patientId}`
          );
        }, 500);
      } catch (error: any) {
        console.error("Erro ao processar fotos:", error);
        toast.error(error.message || "Erro ao processar fotos");
      } finally {
        setUploading(false);
      }
      return;
    }

    // Se for uma foto só, seguir o fluxo normal
    setShowCamera(false);
    const file = files[0];
    setUploading(true);
    setProgress(0);
    setProgressText("Iniciando...");

    try {
      const sessionId = await createSmartfileSession(patientId);

      await processFileUploadUltraFast(sessionId, file, (stage, prog) => {
        setProgressText(stage);
        setProgress(prog);
      }, { skipAIInsights: disableAI });

      // ✅ VALIDAÇÃO: Verificar se o documento é "other" após processamento
      const session = await getSmartfileSession(sessionId);
      if (session.content_type === 'other') {
        // Deletar arquivo do storage se existir
        if (session.file_path) {
          try {
            await supabase.storage
              .from('patient-docs')
              .remove([session.file_path]);
          } catch (storageError) {
            console.error('Erro ao deletar arquivo do storage:', storageError);
          }
        }
        
        // Deletar lab_results associados se existirem
        try {
          await supabase
            .from('lab_results_onboarding')
            .delete()
            .eq('source_session_id', sessionId);
        } catch (labError) {
          console.error('Erro ao deletar lab_results:', labError);
        }
        
        // Deletar a sessão completamente
        try {
          await supabase
            .from('smartfile_sessions')
            .delete()
            .eq('id', sessionId);
        } catch (sessionError) {
          console.error('Erro ao deletar sessão:', sessionError);
        }
        
        toast.error("O arquivo enviado não possui dados clínicos de saúde. Favor verificar.");
        setUploading(false);
        return;
      }

      toast.success("Foto processada com sucesso!");
      onComplete(sessionId);
    } catch (error: any) {
      console.error("Erro ao processar foto:", error);
      toast.error(error.message || "Erro ao processar foto");
    } finally {
      setUploading(false);
    }
  };

  const handleAudioRecord = () => {
    setShowRecorder(true);
  };

  const handleRecordingComplete = async (file: File) => {
    setShowRecorder(false);

    setUploading(true);
    setProgress(0);
    setProgressText("Iniciando...");

    try {
      const sessionId = await createSmartfileSession(patientId);

      await processFileUploadUltraFast(sessionId, file, (stage, prog) => {
        setProgressText(stage);
        setProgress(prog);
      }, { skipAIInsights: disableAI });

      // ✅ VALIDAÇÃO: Verificar se o documento é "other" após processamento
      const session = await getSmartfileSession(sessionId);
      if (session.content_type === 'other') {
        // Deletar arquivo do storage se existir
        if (session.file_path) {
          try {
            await supabase.storage
              .from('patient-docs')
              .remove([session.file_path]);
          } catch (storageError) {
            console.error('Erro ao deletar arquivo do storage:', storageError);
          }
        }
        
        // Deletar lab_results associados se existirem
        try {
          await supabase
            .from('lab_results_onboarding')
            .delete()
            .eq('source_session_id', sessionId);
        } catch (labError) {
          console.error('Erro ao deletar lab_results:', labError);
        }
        
        // Deletar a sessão completamente
        try {
          await supabase
            .from('smartfile_sessions')
            .delete()
            .eq('id', sessionId);
        } catch (sessionError) {
          console.error('Erro ao deletar sessão:', sessionError);
        }
        
        toast.error("O arquivo enviado não possui dados clínicos de saúde. Favor verificar.");
        setUploading(false);
        return;
      }

      toast.success("Áudio processado com sucesso!");
      onComplete(sessionId);
    } catch (error: any) {
      console.error("Erro ao processar áudio:", error);
      toast.error(error.message || "Erro ao processar áudio");
    } finally {
      setUploading(false);
    }
  };

  const handleTextInput = async (text: string) => {
    setShowTextInput(false);

    setUploading(true);
    setProgress(0);
    setProgressText("Criando sessão...");

    try {
      // 1. Criar sessão
      const sessionId = await createSmartfileSession(patientId);
      setProgress(10);

      // 2. Processar texto usando o mesmo fluxo do arquivo
      await processTextInputUltraFast(sessionId, text, (stage, prog) => {
        setProgressText(stage);
        setProgress(prog);
      }, { skipAIInsights: disableAI });

      // ✅ VALIDAÇÃO: Verificar se o documento é "other" após processamento
      const session = await getSmartfileSession(sessionId);
      if (session.content_type === 'other') {
        // Deletar lab_results associados se existirem
        try {
          await supabase
            .from('lab_results_onboarding')
            .delete()
            .eq('source_session_id', sessionId);
        } catch (labError) {
          console.error('Erro ao deletar lab_results:', labError);
        }
        
        // Deletar a sessão completamente
        try {
          await supabase
            .from('smartfile_sessions')
            .delete()
            .eq('id', sessionId);
        } catch (sessionError) {
          console.error('Erro ao deletar sessão:', sessionError);
        }
        
        toast.error("O texto inserido não possui dados clínicos de saúde. Favor verificar.");
        setUploading(false);
        return;
      }

      toast.success("Texto processado com sucesso!");
      onComplete(sessionId);
    } catch (error: any) {
      console.error("Erro ao processar texto:", error);
      toast.error(error.message || "Erro ao processar texto");
    } finally {
      setUploading(false);
    }
  };

  if (showCamera) {
    return (
      <CameraCaptureMulti
        onCapture={handlePhotoCapture}
        onCancel={() => setShowCamera(false)}
        patientId={patientId}
      />
    );
  }

  if (showRecorder) {
    return (
      <AudioRecorder
        onRecordingComplete={handleRecordingComplete}
        onCancel={() => setShowRecorder(false)}
      />
    );
  }

  if (showTextInput) {
    return (
      <TextInput
        onSubmit={handleTextInput}
        onCancel={() => setShowTextInput(false)}
        disabled={uploading}
      />
    );
  }

  const isHomeCompact = compactVariant === "home";
  const compactContainerClass = isHomeCompact
    ? "grid grid-cols-2 gap-x-6 gap-y-5 max-w-[240px] mx-auto sm:mx-0 sm:max-w-none sm:flex sm:flex-wrap sm:items-start sm:gap-4"
    : "grid grid-cols-2 gap-x-6 gap-y-5 max-w-[240px] sm:max-w-none sm:flex sm:flex-wrap sm:items-start sm:gap-4";
  const compactButtonClass = "group inline-flex w-[92px] h-auto flex-col items-center gap-2 p-0 hover:bg-transparent disabled:opacity-50";
  const compactIconContainerClass = "flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary transition-colors group-hover:bg-primary/15";
  const compactIconClass = "h-6 w-6";
  const compactLabelClass = "line-clamp-2 text-center text-[12px] sm:text-[13px] font-semibold leading-tight text-foreground";

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple={currentPlan?.max_file_uploads ? currentPlan.max_file_uploads > 1 : false}
        accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.csv,.mp3,.wav,.m4a"
        onChange={handleFileSelected}
        className="hidden"
      />

      {uploading && (
        <Card className="mb-6 border-purple-500">
          <CardContent className="pt-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{progressText}</span>
                <span className="text-sm text-muted-foreground">{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          </CardContent>
        </Card>
      )}

      {compact ? (
        <div className={compactContainerClass}>
          <Button
            variant="ghost"
            onClick={handleCameraCapture}
            disabled={uploading}
            className={compactButtonClass}
          >
            <span className={compactIconContainerClass}>
              <Camera className={compactIconClass} aria-hidden />
            </span>
            <span className={compactLabelClass}>
              Câmera
            </span>
          </Button>

          <Button
            variant="ghost"
            onClick={handleAudioRecord}
            disabled={uploading}
            className={compactButtonClass}
          >
            <span className={compactIconContainerClass}>
              <Mic className={compactIconClass} aria-hidden />
            </span>
            <span className={compactLabelClass}>
              Áudio
            </span>
          </Button>

          <Button
            variant="ghost"
            onClick={handleFileUpload}
            disabled={uploading}
            className={compactButtonClass}
          >
            <span className={compactIconContainerClass}>
              {uploading ? (
                <Loader2 className={`${compactIconClass} animate-spin`} aria-hidden />
              ) : (
                <Upload className={compactIconClass} aria-hidden />
              )}
            </span>
            <span className={compactLabelClass}>
              Arquivo
            </span>
          </Button>

          <Button
            variant="ghost"
            onClick={() => setShowTextInput(true)}
            disabled={uploading}
            className={compactButtonClass}
          >
            <span className={compactIconContainerClass}>
              <FileText className={compactIconClass} aria-hidden />
            </span>
            <span className={compactLabelClass}>
              Texto
            </span>
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <button
            className={`min-h-[140px] bg-background border border-border hover:border-transparent hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:bg-gradient-to-br hover:from-primary/5 hover:to-secondary/5 rounded-xl p-6 flex flex-col items-center justify-center gap-3 transition-all duration-300 hover:scale-[1.02] ${uploading ? "opacity-50 pointer-events-none" : ""
              }`}
            onClick={handleCameraCapture}
            disabled={uploading}
          >
            <div className="p-3 rounded-full bg-primary/10">
              <Camera className="h-8 w-8 text-primary" />
            </div>
            <span className="text-sm font-semibold">Câmera</span>
          </button>

          <button
            className={`min-h-[140px] bg-background border border-border hover:border-transparent hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:bg-gradient-to-br hover:from-primary/5 hover:to-secondary/5 rounded-xl p-6 flex flex-col items-center justify-center gap-3 transition-all duration-300 hover:scale-[1.02] ${uploading ? "opacity-50 pointer-events-none" : ""
              }`}
            onClick={handleAudioRecord}
            disabled={uploading}
          >
            <div className="p-3 rounded-full bg-primary/10">
              <Mic className="h-8 w-8 text-primary" />
            </div>
            <span className="text-sm font-semibold">Áudio</span>
          </button>

          <button
            className={`min-h-[140px] bg-background border border-border hover:border-transparent hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:bg-gradient-to-br hover:from-primary/5 hover:to-secondary/5 rounded-xl p-6 flex flex-col items-center justify-center gap-3 transition-all duration-300 hover:scale-[1.02] ${uploading ? "opacity-50 pointer-events-none" : ""
              }`}
            onClick={handleFileUpload}
            disabled={uploading}
          >
            {uploading ? (
              <>
                <div className="p-3 rounded-full bg-primary/10">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
                <span className="text-sm font-semibold">Processando...</span>
              </>
            ) : (
              <>
                <div className="p-3 rounded-full bg-primary/10">
                  <Upload className="h-8 w-8 text-primary" />
                </div>
                <span className="text-sm font-semibold">Arquivo</span>
              </>
            )}
          </button>

          <button
            className={`min-h-[140px] bg-background border border-border hover:border-transparent hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:bg-gradient-to-br hover:from-primary/5 hover:to-secondary/5 rounded-xl p-6 flex flex-col items-center justify-center gap-3 transition-all duration-300 hover:scale-[1.02] ${uploading ? "opacity-50 pointer-events-none" : ""
              }`}
            onClick={() => setShowTextInput(true)}
            disabled={uploading}
          >
            <div className="p-3 rounded-full bg-primary/10">
              <FileText className="h-8 w-8 text-primary" />
            </div>
            <span className="text-sm font-semibold">Texto</span>
          </button>
        </div>
      )}

      {onCancel && (
        <Button
          variant="outline"
          onClick={onCancel}
          className="w-full mt-4"
        >
          Cancelar
        </Button>
      )}
    </>
  );
}
